import { asPartialDef } from "../coerce";
import type { BlockPlugin, GradeResult, ValidationResult } from "../types";

/** Umbral por defecto de visionado para dar el bloque por completado. */
export const DEFAULT_MIN_WATCH_PCT = 0.9;

export interface VideoDef {
  /** Id del `video_asset` a reproducir. */
  videoAssetId: string;
  /** Fracción [0,1] que hay que ver para completar. Default 0.9. */
  minWatchPct?: number;
}

/**
 * Respuesta del alumno: el estado de visionado REPORTADO (alimentado por
 * `video_watch_progress`). No es una "respuesta" evaluable: el bloque se
 * completa al cruzar `minWatchPct`.
 */
export type VideoResponse = { watchedPct: number };

function clamp01(n: number): number {
  if (typeof n !== "number" || Number.isNaN(n)) {
    return 0;
  }
  return Math.min(1, Math.max(0, n));
}

/**
 * Bloque `video`: reproduce un `video_asset` y se completa por VISIONADO (no por
 * respuesta). `interaction: "info"` + `autoGradable: false` (mirror de los
 * bloques no evaluables): el antifraude sabe que no aporta nota fiable; el
 * "grade" solo refleja si se alcanzó el umbral de visionado.
 */
export const videoBlock: BlockPlugin<VideoDef, VideoResponse> = {
  type: "video",
  interaction: "info",
  autoGradable: false,

  validate(def: unknown): ValidationResult<VideoDef> {
    const d = asPartialDef<VideoDef>(def);
    if (!d) {
      return { ok: false, errors: ["video: definición no es un objeto"] };
    }
    const errors: string[] = [];
    if (typeof d.videoAssetId !== "string" || d.videoAssetId.trim() === "") {
      errors.push("video: `videoAssetId` requerido");
    }
    if (d.minWatchPct !== undefined) {
      if (typeof d.minWatchPct !== "number" || d.minWatchPct < 0 || d.minWatchPct > 1) {
        errors.push("video: `minWatchPct` debe estar en [0,1]");
      }
    }
    if (errors.length > 0) {
      return { ok: false, errors };
    }
    return {
      ok: true,
      def: {
        videoAssetId: d.videoAssetId ?? "",
        minWatchPct: d.minWatchPct ?? DEFAULT_MIN_WATCH_PCT,
      },
    };
  },

  grade(def: VideoDef, response: VideoResponse | undefined): GradeResult {
    const min = clamp01(def.minWatchPct ?? DEFAULT_MIN_WATCH_PCT);
    if (!response) {
      return { correct: false, score: 0, maxScore: 1 };
    }
    const watched = clamp01(response.watchedPct);
    const complete = watched >= min;
    return { correct: complete, score: complete ? 1 : 0, maxScore: 1 };
  },
};
